import { Component, Inject, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {MatIconModule} from "@angular/material/icon";
import {FormsModule} from "@angular/forms";

export type StoryVideoDraft = {
    file: File;
    caption: string;
    muted: boolean;
    startMs: number;
    endMs: number;
};

export type VideoComposerData = {
    file: File;
    maxDurationSec?: number; // default 30
};

@Component({
    selector: 'app-video-story-composer-dialog',
    templateUrl: './video-story-composer.dialog.html',
    styleUrls: ['./video-story-composer.dialog.scss'],
    imports: [
    MatIconModule,
    FormsModule
]
})
export class VideoStoryComposerDialogComponent implements AfterViewInit, OnDestroy {
    @ViewChild('videoEl', { static: false }) videoEl?: ElementRef<HTMLVideoElement>;

    url = '';
    loading = true;

    maxDurationSec = 30;

    totalSec = 1;
    startSec = 0;
    endSec = 1;

    muted = false;
    caption = '';
    playing = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: VideoComposerData,
        private dialogRef: MatDialogRef<VideoStoryComposerDialogComponent>
    ) {
        this.maxDurationSec = data.maxDurationSec ?? 30;
        this.url = URL.createObjectURL(data.file);
    }

    ngAfterViewInit(): void {
        const v = this.videoEl?.nativeElement;
        if (!v) return;

        v.src = this.url;
        v.preload = 'metadata';
        v.muted = this.muted;

        v.onloadedmetadata = () => {
            this.totalSec = Math.max(1, v.duration || 1);

            this.startSec = 0;
            this.endSec = Math.min(this.totalSec, this.maxDurationSec);

            this.loading = false;
            v.currentTime = this.startSec;
        };

        // keep playback in selected range
        v.ontimeupdate = () => {
            if (v.currentTime > this.endSec) {
                v.pause();
                v.currentTime = this.startSec;
            }
        };
        v.onplay = () => this.playing = true;
        v.onpause = () => this.playing = false;
        v.onended = () => this.playing = false;
    }

    ngOnDestroy(): void {
        try { URL.revokeObjectURL(this.url); } catch {}
    }

    close(): void {
        this.dialogRef.close(null);
    }

    togglePlay(): void {
        const v = this.videoEl?.nativeElement;
        if (!v) return;

        if (!v.paused) {
            v.pause();
            return;
        }
        v.currentTime = this.startSec;
        v.play().catch(() => this.playing = false);
    }

    isPlaying(): boolean {
        return this.playing;
    }

    toggleMute(): void {
        this.muted = !this.muted;
        const v = this.videoEl?.nativeElement;
        if (v) v.muted = this.muted;
    }

    onStartChange(value: number): void {
        let s = clamp(value, 0, this.totalSec);
        let e = this.endSec;

        if (e - s > this.maxDurationSec) {
            e = clamp(s + this.maxDurationSec, 0, this.totalSec);
        }
        if (e < s) e = s;

        this.startSec = s;
        this.endSec = e;

        const v = this.videoEl?.nativeElement;
        if (v) v.currentTime = this.startSec;
    }

    onEndChange(value: number): void {
        let e = clamp(value, 0, this.totalSec);
        let s = this.startSec;

        if (e - s > this.maxDurationSec) {
            s = clamp(e - this.maxDurationSec, 0, this.totalSec);
        }
        if (e < s) e = s;

        this.startSec = s;
        this.endSec = e;

        const v = this.videoEl?.nativeElement;
        if (v) v.currentTime = this.startSec;
    }

    durationLabel(): string {
        const sec = Math.max(0, this.endSec - this.startSec);
        const mm = String(Math.floor(sec / 60)).padStart(2, '0');
        const ss = String(Math.floor(sec % 60)).padStart(2, '0');
        return `${mm}:${ss}`;
    }

    timeLabel(value: number): string {
        const safeValue = Math.max(0, value || 0);
        const minutes = String(Math.floor(safeValue / 60)).padStart(2, '0');
        const seconds = String(Math.floor(safeValue % 60)).padStart(2, '0');
        return `${minutes}:${seconds}`;
    }

    canSend(): boolean {
        return !this.loading && this.endSec > this.startSec;
    }

    send(): void {
        const draft: StoryVideoDraft = {
            file: this.data.file,
            caption: (this.caption || '').trim(),
            muted: this.muted,
            startMs: Math.round(this.startSec * 1000),
            endMs: Math.round(this.endSec * 1000),
        };
        this.dialogRef.close(draft);
    }
}

function clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, n));
}
